import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { moreCandidatesPreview } from '@/modules/hiring-manager/utils/moreCandidates';
import { useSourceJobMutation } from '@/modules/jobs/mutations/useSourceJobMutation';
import { jobsQueryKeys } from '@/modules/jobs/queries/queryKeys';
import { useSourcingAllowanceQuery } from '@/modules/jobs/queries/useSourcingAllowanceQuery';
import type { JobApi } from '@/modules/jobs/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/modules/ui/components/alert-dialog/AlertDialog';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

interface FindMoreCandidatesProps {
  job: JobApi;
  /** Where the user changes their plan once the allowance is used up. */
  billingHref: string;
}

/**
 * Another round of the job's finished search. What it adds and uses of the workspace's sourcing allowance shows
 * before it starts, as the Finlay agent previews it in chat, and the round takes no more than the preview said.
 */
export function FindMoreCandidates({
  job,
  billingHref,
}: FindMoreCandidatesProps) {
  const [open, setOpen] = useState(false);
  const allowanceQuery = useSourcingAllowanceQuery(job.id, { enabled: open });
  const sourceJob = useSourceJobMutation();
  const queryClient = useQueryClient();
  const preview = allowanceQuery.data
    ? moreCandidatesPreview(allowanceQuery.data)
    : undefined;

  const findCandidates = (size: number) =>
    sourceJob.mutate(
      { jobId: job.id, continueSourcing: true, maxCandidatesCount: size },
      {
        onSuccess: () => {
          // The state flips back to Sourcing, and the allowance shows what this round took.
          void queryClient.invalidateQueries({
            queryKey: jobsQueryKeys.detail(job.id),
          });
          setOpen(false);
          toast.success(
            `Finlay is finding up to ${size} new candidates. They’ll appear here within a few minutes.`,
          );
        },
      },
    );

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (sourceJob.isPending) return;
        if (next) sourceJob.reset();
        setOpen(next);
      }}
    >
      <AlertDialogTrigger asChild>
        <Button className="h-8 rounded-md" size="sm" variant="outline">
          Find more candidates
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Find more candidates</AlertDialogTitle>
          <AlertDialogDescription>
            {preview
              ? preview.text
              : allowanceQuery.isError
                ? 'Your sourcing allowance couldn’t be checked just now. Try again in a moment.'
                : 'Checking your sourcing allowance…'}
            {preview?.size === 0 && (
              <>
                {' '}
                <Link className="text-primary underline" to={billingHref}>
                  Upgrading your plan
                </Link>{' '}
                adds more.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {sourceJob.error && (
          <InlineError>
            {actionErrorMessage('find more candidates', sourceJob.error)}
          </InlineError>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={sourceJob.isPending}>
            Cancel
          </AlertDialogCancel>
          {preview && preview.size > 0 && (
            <AlertDialogAction
              aria-busy={sourceJob.isPending}
              disabled={sourceJob.isPending}
              onClick={(event) => {
                // Stays open until the round has started, or says why it didn't.
                event.preventDefault();
                findCandidates(preview.size);
              }}
            >
              {sourceJob.isPending ? 'Starting…' : 'Find candidates'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
